import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren
} from "@angular/core";
import {MenuModel} from "../menu.model";
import {MenuEvents} from "../menu-events";
import {Observable, Subscription} from "rxjs";
import {ButtonProminence} from "../../buttons/button/button";

@Component({
    selector: "woa-menu-item-selection-panel",
    templateUrl: "./menu-item-selection-panel.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuItemSelectionPanelComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly MIN_ITEM_WIDTH: number = 375;
    private static readonly ITEMS_SCROLLING_COMPLETE_TIMEOUT_MS: number = 250;

    @Input() showBackButton: boolean = true;
    @Input() showClosePromoAction: boolean = false;
    @Input() alwaysShowCategoryHeader: boolean = false;
    @Input() pageScroller: ElementRef;

    @Output() onBack: EventEmitter<void> = new EventEmitter();

    @ViewChildren("anchorTarget") protected anchorTargets: QueryList<ElementRef>;

    ButtonProminence = ButtonProminence;

    private _anchorPanel: ElementRef;
    private anchorTargetObserver: IntersectionObserver;
    private anchorTargetVisible: boolean[] = [];
    public anchorTargetHasBeenVisible: boolean[] = [];
    private observedAnchorTargets: ElementRef[];
    private scrollingToSelectedAnchorTarget: boolean = false;
    private itemsScrollingStopped$: Observable<void> = MenuEvents.itemsScrolling$.debounceTime(MenuItemSelectionPanelComponent.ITEMS_SCROLLING_COMPLETE_TIMEOUT_MS);
    private subscriptions: Subscription = new Subscription();

    constructor(public menuModel: MenuModel,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.subscriptions.add(MenuEvents.menuBuilt$.subscribe(() => {
            this.setupAnchorTargetObserver();
            this.changeDetectorRef.detectChanges();
        }));
        this.subscriptions.add(MenuEvents.anchorSelected$.subscribe((direct: boolean) => {
            if (direct) {
                this.scrollToSelectedAnchorTarget();
            }
        }));

        this.subscriptions.add(this.itemsScrollingStopped$.subscribe(() => this.checkScrollingComplete()));
    }

    ngAfterViewInit(): void {
        this.setupAnchorTargetObserver();
        this.subscriptions.add(this.anchorTargets.changes.subscribe(() => this.observeAnchorTargets()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    @Input()
    get anchorPanel(): ElementRef {
        return this._anchorPanel;
    }

    set anchorPanel(anchorPanel: ElementRef) {
        this._anchorPanel = anchorPanel;
        this.setupAnchorTargetObserver();
    }

    get showMainCategory(): boolean {
        return this.alwaysShowCategoryHeader || this.menuModel.selectedCategoryHasMainCategoryItems;
    }

    get showMainCategoryHeader(): boolean {
        return this.alwaysShowCategoryHeader ||
            (this.menuModel.selectedCategoryHasMainCategoryItems && (!!this.menuModel.selectedCategorySubcategories.length || !this.menuModel.selectedCategory.id));
    }

    get showMainCategoryImage(): boolean {
        return this.menuModel.showSubcategoryImages &&
            (!this.menuModel.showCategoryImages || (!this.menuModel.selectedCategory.id && !this.alwaysShowCategoryHeader)) && !!this.menuModel.getCategoryImage(this.menuModel.selectedCategory);
    }

    get anchorPanelHeight(): number {
        return this._anchorPanel ? this._anchorPanel.nativeElement.offsetHeight || 0 : 0;
    }

    private setupAnchorTargetObserver(): void {
        if (this.anchorTargetObserver) {
            this.anchorTargetObserver.disconnect();
        }
        if (this.anchorTargets && this._anchorPanel && this.anchorPanelHeight) {
            this.anchorTargetObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => this.trackAnchorTargetIntersections(entries), {
                root: this.pageScroller ? this.pageScroller.nativeElement : null,
                rootMargin: "-" + (this.anchorPanelHeight - 10) + "px 0px 0px 0px",
                threshold: [0, 0.25, 0.5, 0.75, 1]
            });
            this.observeAnchorTargets();
        }
    }

    private observeAnchorTargets(): void {
        if (this.anchorTargetObserver) {
            this.observedAnchorTargets = this.anchorTargets.toArray();
            if (this.showMainCategory && !this.menuModel.selectedCategoryHasMainCategoryItems) {
                this.observedAnchorTargets.shift();
            }
            this.anchorTargetObserver.disconnect();
            this.observedAnchorTargets.forEach((anchor: ElementRef) => this.anchorTargetObserver.observe(anchor.nativeElement));
        }
    }

    private trackAnchorTargetIntersections(entries: IntersectionObserverEntry[]) {
        entries.forEach((entry: IntersectionObserverEntry) => {
            this.observedAnchorTargets.some((anchor: ElementRef, index: number) => {
                if (anchor.nativeElement === entry.target) {
                    this.anchorTargetVisible[index] = entry.isIntersecting;

                    // Lazy initialisation of menu items
                    if (entry.isIntersecting && !this.anchorTargetHasBeenVisible[index]) {
                        this.anchorTargetHasBeenVisible[index] = true;
                        this.changeDetectorRef.detectChanges();
                    }
                    return true;
                }
                return false;
            });
        });
        MenuEvents.itemsScrolling$.next();

        // We want to auto-scroll the anchor bar in response to the user manually scrolling the items.
        // However, when an anchor is selected by the user, this will both directly select the anchor and also trigger scrolling of the items.
        // We don't want to select intermediate anchors during this scrolling, so there is a timeout here after user selection of the anchor,
        // before which item scrolling will trigger anchor selection.
        if (this.menuModel.canAutoScrollAnchorBar) {
            this.observedAnchorTargets.some((anchor: ElementRef, anchorIndex: number) => {
                if (this.anchorTargetVisible[anchorIndex]) {
                    this.menuModel.selectAnchor(anchorIndex, true);
                    return true;
                }
                return false;
            });
        }
    }

    private get selectedAnchorTarget(): ElementRef {
        return this.menuModel.selectedAnchorIndex === undefined || !this.observedAnchorTargets ? undefined : this.observedAnchorTargets[this.menuModel.selectedAnchorIndex];
    }

    private scrollToSelectedAnchorTarget(): void {
        if (this.selectedAnchorTarget) {
            this.scrollingToSelectedAnchorTarget = true;
            this.selectedAnchorTarget.nativeElement.scrollIntoView({
                block: "start",
                inline: "nearest",
                behavior: "smooth"
            });
        }
    }

    private checkScrollingComplete(): void {

        // itemsScrollingStopped$ will fire when we stop receiving itemScrolling$ events due to observed intersection changes
        // for a period of ITEMS_SCROLLING_COMPLETE_TIMEOUT_MS.
        // Unfortunately, this may not always be sufficient to determine that scrolling is not still happening,
        // depending upon the number of items in each subcategory and the speed at which the browser actually scrolls.
        // For this reason, we also check whether the expected anchor target is actually in view before starting to scroll the anchor bar.
        // Note that scrolling the anchor bar stop a programmatically initiated scroll of the items.
        if (this.scrollingToSelectedAnchorTarget && this.selectedAnchorTarget) {
            if (!this.anchorTargetVisible[this.menuModel.selectedAnchorIndex]) {
                return;
            }
            this.scrollingToSelectedAnchorTarget = false;
        }

        // Notify the anchor bar to scroll once scrolling of the menu items has actually completed.
        MenuEvents.scrollAnchorBar$.next(this.menuModel.selectedAnchorIndex);
    }

    itemColumns(items: HTMLElement): number {
        return Math.floor(items.clientWidth / MenuItemSelectionPanelComponent.MIN_ITEM_WIDTH);
    }
}



// WEBPACK FOOTER //
// ./src/app/menu/menu-item-selection-panel/menu-item-selection-panel.component.ts